import { type ReactNode, useMemo } from "react";
import { toast as soonerToast } from "sonner";

import { type AlertRootProps, BasicAlert } from "../../components";

export type ToastMessage = string | { title: string; description?: string | ReactNode };

type Position = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top-center" | "bottom-center";

export type ToastOptions = {
  position?: Position;
  duration?: number;
  dismissible?: boolean;
};

const useToast = () =>
  useMemo(() => {
    const toast = (type: AlertRootProps["type"], message: ToastMessage, options?: ToastOptions) =>
      soonerToast.custom(id => {
        const title = typeof message === "string" ? message : message.title;
        const handleClose = [undefined, true].includes(options?.dismissible)
          ? () => soonerToast.dismiss(id)
          : undefined;

        return (
          <BasicAlert
            key={id}
            type={type}
            title={title}
            className="w-[21.5rem] max-w-[21.5rem] whitespace-pre-wrap"
            onClose={handleClose}
          >
            {typeof message !== "string" ? message.description : null}
          </BasicAlert>
        );
      }, options);

    return {
      custom: soonerToast.custom,
      success: (message: ToastMessage, options?: ToastOptions) => toast("success", message, options),
      warning: (message: ToastMessage, options?: ToastOptions) => toast("warning", message, options),
      primary: (message: ToastMessage, options?: ToastOptions) => toast("primary", message, options),
      secondary: (message: ToastMessage, options?: ToastOptions) => toast("secondary", message, options),
      error: (message: ToastMessage, options?: ToastOptions) => toast("error", message, options),
    };
  }, []);

export default useToast;
